export function plankHash(n) { n = Math.imul(n ^ (n >>> 16), 0x45d9f3b); n = Math.imul(n ^ (n >>> 16), 0x45d9f3b); return ((n ^ (n >>> 16)) >>> 0) / 4294967296; }
export function rectangularPlankRows(span, cross, length, width, seed, minimumEnd = .3, minimumStagger = .3, balance = true) {
    if (![span, cross, length, width, minimumEnd, minimumStagger].every(Number.isFinite) || Math.min(span, cross, length, width) <= 0 || length < minimumEnd || span < minimumEnd * 2 || minimumStagger < 0 || minimumStagger > .5)
        return null;
    const count = Math.ceil(cross / width - 1e-9);
    if (count > 250 || Math.ceil(span / length) * count > 6000)
        return null;
    const edgeWidth = count === 1 ? cross : (cross - (count - 2) * width) / 2, rows = [];
    const valid = (first) => first >= minimumEnd && first <= length && first <= span - minimumEnd && ((span - first) % length) >= minimumEnd - 1e-9;
    const choices = Array.from({ length: count }, (_, row) => Array.from({ length: 256 }, (_, i) => minimumEnd + (length - minimumEnd) * plankHash(seed ^ Math.imul(row + 31, 2654435761) ^ Math.imul(i + 73, 2246822519))).filter(valid));
    let attempts = 0;
    function search(row) {
        if (row === count)
            return true;
        if (++attempts > 20000)
            return false;
        const previous = rows[row - 1]?.first, earlier = rows[row - 2]?.first;
        const additional = previous === undefined ? [] : [(previous + length * .5) % length];
        const available = [...choices[row], ...additional.filter(valid)].filter(first => { if (previous === undefined)
            return true; const d = Math.abs(first - previous); return Math.min(d, length - d) >= minimumStagger * length - 1e-9; });
        if (earlier !== undefined)
            available.sort((a, b) => { const da = Math.abs(a - earlier), db = Math.abs(b - earlier); return Math.min(db, length - db) - Math.min(da, length - da); });
        const pool = available.slice(0, Math.max(12, Math.ceil(available.length * .65)));
        for (let i = pool.length - 1; i > 0; i--) {
            const j = Math.floor(plankHash(seed ^ Math.imul(row + 101, 3266489917) ^ Math.imul(i + 107, 668265263)) * (i + 1));
            [pool[i], pool[j]] = [pool[j], pool[i]];
        }
        for (const first of [...pool, ...available.slice(pool.length)]) {
            const pieces = [];
            let start = 0, part = first;
            while (start < span - 1e-9) {
                const size = Math.min(part, span - start);
                pieces.push({ start, length: size });
                start += size;
                part = length;
            }
            const crossStart = balance ? (row === 0 ? 0 : edgeWidth + (row - 1) * width) : row * width, crossWidth = balance ? (row === 0 || row === count - 1 ? edgeWidth : width) : Math.min(width, cross - crossStart);
            rows[row] = { first, pieces, crossStart, crossWidth };
            if (search(row + 1))
                return true;
        }
        rows.length = row;
        return false;
    }
    return search(0) ? rows : null;
}
export function rectangularRoom(shape, holes = []) {
    if (shape.length !== 4 || holes.length || shape.some(p => !Number.isFinite(p.x) || !Number.isFinite(p.y)))
        return false;
    const x = shape.map(p => p.x), y = shape.map(p => p.y), minX = Math.min(...x), maxX = Math.max(...x), minY = Math.min(...y), maxY = Math.max(...y);
    if (maxX - minX < .001 || maxY - minY < .001)
        return false;
    if (new Set(shape.map(p => `${p.x.toFixed(5)},${p.y.toFixed(5)}`)).size !== 4)
        return false;
    const area = Math.abs(shape.reduce((n, p, i) => { const q = shape[(i + 1) % shape.length]; return n + p.x * q.y - q.x * p.y; }, 0)) / 2;
    return Math.abs(area - (maxX - minX) * (maxY - minY)) < 1e-5 && shape.every(p => (Math.abs(p.x - minX) < 1e-5 || Math.abs(p.x - maxX) < 1e-5) && (Math.abs(p.y - minY) < 1e-5 || Math.abs(p.y - maxY) < 1e-5));
}
/** An axis-aligned room (every edge horizontal or vertical): an L-shaped hall, a kitchen with a return. The plan clips the
 *  floor's one run to the polygon, so the shape only has to be rectilinear, not a rectangle (Jacky, 3 Nov 2026: planks at
 *  their real size, laid on the plan, no line where there is no wall). */
export function rectilinearRoom(shape, holes = []) {
    if (shape.length < 4 || shape.some(p => !Number.isFinite(p.x) || !Number.isFinite(p.y)))
        return false;
    const x = shape.map(p => p.x), y = shape.map(p => p.y);
    if (Math.max(...x) - Math.min(...x) < .001 || Math.max(...y) - Math.min(...y) < .001)
        return false;
    for (let i = 0; i < shape.length; i++) {
        const a = shape[i], b = shape[(i + 1) % shape.length];
        const dx = Math.abs(a.x - b.x), dy = Math.abs(a.y - b.y);
        if (dx < 1e-5 && dy < 1e-5)
            return false;
        if (dx > 1e-5 && dy > 1e-5)
            return false;
    }
    return holes.length === 0 || rectilinearHoles(holes);
}
function rectilinearHoles(holes) { return holes.every(h => Array.isArray(h) && rectilinearRoom(h)); }
/** The box one floor's run is generated over: every room in scope, so the planks carry through doorways and open-plan
 *  joins as one floor. */
export function floorRunBox(rooms) {
    const pts = rooms.flatMap(r => r.shape);
    if (!pts.length)
        return null;
    const xs = pts.map(p => p.x), ys = pts.map(p => p.y), x = Math.min(...xs), y = Math.min(...ys);
    return { x, y, width: Math.max(...xs) - x, height: Math.max(...ys) - y };
}
/**
 * Classic herringbone: blocks at 45° to the walls, the zigzag spine running the length of the room. Jacky, 3 Nov 2026 (Belmore):
 * "herringbone line start top bottom … square to hallway wall, balanced both side" — the spine is set square to the hallway
 * wall, i.e. parallel to it down the hall's centre, so the cuts at both hallway walls match. Generated as the square
 * herringbone lattice (H at [0,L]×[0,W], V at [L,L+W]×[W−L,W], translations (W,W) and (L+W,W−L)) and rotated 45°, so the
 * blocks are the product's real size in metres. Never an order or cutting plan.
 * @param box the floor run box in metres
 * @param length block length (m), @param width block width (m)
 * @param spine the spine's position across the run (x when vertical, y when horizontal), in floor metres
 * @param vertical true: spine runs along y (top to bottom of the plan)
 */
export function herringboneBlocks(box, length, width, spine, vertical) {
    if (![box.x, box.y, box.width, box.height, length, width, spine].every(Number.isFinite) || Math.min(box.width, box.height, length, width) <= 0 || length <= width)
        return null;
    const L = length, W = width, s2 = Math.SQRT2;
    // local frame: X across the spine, Y along it
    const bx = vertical ? box.x : box.y, by = vertical ? box.y : box.x, bw = vertical ? box.width : box.height, bh = vertical ? box.height : box.width;
    const cx = spine - (L - W / 2) / s2, cy = by;
    const toLocal = (p, q) => ({ X: cx + (p - q) / s2, Y: cy + (p + q) / s2 });
    const corners = [[bx, by], [bx + bw, by], [bx, by + bh], [bx + bw, by + bh]].map(([X, Y]) => { const dx = X - cx, dy = Y - cy; return { p: (dx + dy) / s2, q: (dy - dx) / s2 }; });
    const pmin = Math.min(...corners.map(c => c.p)), pmax = Math.max(...corners.map(c => c.p)), qmin = Math.min(...corners.map(c => c.q)), qmax = Math.max(...corners.map(c => c.q));
    const margin = L + W, blocks = [];
    const jFrom = Math.floor((pmin - qmax - 2 * margin) / (2 * L)), jTo = Math.ceil((pmax - qmin + 2 * margin) / (2 * L));
    for (let j = jFrom; j <= jTo; j++) {
        const iFrom = Math.floor((pmin - margin - j * (L + W)) / W), iTo = Math.ceil((pmax + margin - j * (L + W)) / W);
        for (let i = iFrom; i <= iTo; i++) {
            const p0 = i * W + j * (L + W), q0 = i * W + j * (W - L);
            for (const [kind, rect] of [['a', [[p0, q0], [p0 + L, q0], [p0 + L, q0 + W], [p0, q0 + W]]], ['b', [[p0 + L, q0 + W - L], [p0 + L + W, q0 + W - L], [p0 + L + W, q0 + W], [p0 + L, q0 + W]]]]) {
                const local = rect.map(([p, q]) => toLocal(p, q));
                if (local.every(c => c.X < bx) || local.every(c => c.X > bx + bw) || local.every(c => c.Y < by) || local.every(c => c.Y > by + bh))
                    continue;
                blocks.push({ kind, points: local.map(c => vertical ? { x: c.X, y: c.Y } : { x: c.Y, y: c.X }) });
                if (blocks.length > 20000)
                    return null;
            }
        }
    }
    return blocks;
}
/** The traced walls with every doorway cut out of them, so a door reads as an opening in the wall line rather than a wall
 *  (Jacky, 3 Nov 2026: "show doorway as opening"). A door is on a wall when both its ends lie within `tolerance` metres of
 *  the wall's line and inside its length; the wall is then drawn in the pieces either side of it. */
export function wallsWithOpenings(walls, doors, tolerance = .08) {
    const out = [];
    for (const wall of walls) {
        const dx = wall.b.x - wall.a.x, dy = wall.b.y - wall.a.y, len = Math.hypot(dx, dy);
        if (!(len > 1e-6)) {
            out.push(wall);
            continue;
        }
        const ux = dx / len, uy = dy / len;
        const along = (p) => (p.x - wall.a.x) * ux + (p.y - wall.a.y) * uy, off = (p) => Math.abs((p.x - wall.a.x) * uy - (p.y - wall.a.y) * ux);
        const cuts = doors.filter(d => off(d.from) <= tolerance && off(d.to) <= tolerance).map(d => [along(d.from), along(d.to)].sort((p, q) => p - q))
            .filter(([t0, t1]) => t1 > -tolerance && t0 < len + tolerance).map(([t0, t1]) => [Math.max(0, t0), Math.min(len, t1)]).filter(([t0, t1]) => t1 - t0 > 1e-6).sort((p, q) => p[0] - q[0]);
        if (!cuts.length) {
            out.push(wall);
            continue;
        }
        let at = 0;
        for (const [t0, t1] of cuts) {
            if (t0 - at > 1e-6)
                out.push({ ...wall, a: { x: wall.a.x + ux * at, y: wall.a.y + uy * at }, b: { x: wall.a.x + ux * t0, y: wall.a.y + uy * t0 } });
            at = Math.max(at, t1);
        }
        if (len - at > 1e-6)
            out.push({ ...wall, a: { x: wall.a.x + ux * at, y: wall.a.y + uy * at }, b: wall.b });
    }
    return out;
}
//# sourceMappingURL=plank-setout.js.map